import { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import SelectBase from '../core/form/SelectBase';
import MultiSelect from '../core/form/MultiSelect';
import ErrorMessage from '../parameters/fields/ErrorMessage';
import FormRow from '../core/form/FormRow';
import PropTypes from 'prop-types';
import { updateCustomModel, deleteCustomModel } from '../../api/registry';
import { confirm } from '@/components/core/createConfirmation';
import { TrashIcon } from '@heroicons/react/24/outline';
import { PERMISSION_OPTIONS } from '@/consts';
import DownloadCustomModelLink from './DownloadCustomModelBtn';
import DuplicateEquationModuleButton from './DuplicateEquationModuleButton';
import { useCustomModel } from '@/stores/nodeStore';
import { getProjects } from '@/api/projects';

const CustomModelForm = () => {
  const [projectOptions, setProjectOptions] = useState([]);
  const customModel = useCustomModel();
  const navigate = useNavigate();
  const { projectId, pathwayId, caseId } = useParams();
  const projectOptionsPresent = projectOptions?.length > 0;

  const {
    register,
    formState: { errors },
    watch,
    control,
    setValue,
  } = useFormContext();

  const permissions = watch('permissions');
  const shouldLoadProjects = permissions === 'project' && !projectOptionsPresent;

  useEffect(() => {
    if (!shouldLoadProjects) return;

    const loadProjects = async () => {
      try {
        const { data } = await getProjects();
        const options = (data?.results ?? [])
          .map(project => ({ value: project.id, label: project.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setProjectOptions(options);
      } catch (err) {
        console.error(err);
      }
    };

    loadProjects();
  }, [shouldLoadProjects]);

  useEffect(() => {
    if (customModel) {
      if (customModel.permissions) {
        setValue('permissions', customModel.permissions);
      }
      if (customModel?.project_ids?.length) {
        setValue('project_ids', customModel.project_ids);
      }
    }
  }, [customModel, setValue]);

  const handlePermissionsChange = e => {
    const newPermissions = e.target.value;
    setValue('permissions', newPermissions, { shouldValidate: true });
    setValue('project_ids', [], { shouldValidate: true });

    updateCustomModel(customModel.id, {
      permissions: newPermissions,
      project_ids: [],
    });
  };

  const handleProjectIdsChange = newProjectIds => {
    setValue('project_ids', newProjectIds, { shouldValidate: true });

    updateCustomModel(customModel.id, {
      permissions: permissions,
      project_ids: newProjectIds,
    });
  };

  const handleDelete = async () => {
    if (await confirm('Are you sure you want to permanently delete this module?\nThis cannot be recovered.')) {
      await deleteCustomModel(customModel.id);
      navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`, { replace: true });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="form-control w-full group node">
          <FormRow label="Permissions" name="permissions" tooltip="Select who can access this model">
            <div className="join w-full">
              <SelectBase
                {...register('permissions', {
                  onChange: handlePermissionsChange,
                })}
                options={PERMISSION_OPTIONS}
                isDefault={false}
              />
            </div>
          </FormRow>
        </div>

        {permissions === 'project' && (
          <div className="form-control w-full group node">
            <FormRow label="Selected Projects" name="project_ids" tooltip="Choose which projects can access this model">
              <div className="join w-full">
                <Controller
                  name="project_ids"
                  control={control}
                  defaultValue={customModel?.project_ids || []}
                  rules={{
                    validate: value => {
                      if (permissions === 'project' && !value?.length) {
                        return 'Please select at least one project';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      options={projectOptions}
                      value={field.value}
                      onChange={value => {
                        field.onChange(value);
                        handleProjectIdsChange(value);
                      }}
                      placeholder="Select projects..."
                      label="project"
                    />
                  )}
                />
              </div>
            </FormRow>
            {errors?.project_ids && (
              <div className="h-5">
                <ErrorMessage text={String(errors.project_ids.message)} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between pt-2">
        <DownloadCustomModelLink customModel={customModel} />
        <DuplicateEquationModuleButton customModel={customModel} />
        <button type="button" onClick={handleDelete} className="btn btn-sm btn-error gap-2">
          <TrashIcon className="h-5 w-5" />
          Delete
        </button>
      </div>
    </div>
  );
};

CustomModelForm.propTypes = {
  customModel: PropTypes.shape({
    permissions: PropTypes.oneOf(['global', 'creator', 'project']),
    project_ids: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default CustomModelForm;
